import { NgIf } from '@angular/common';
import {
	ChangeDetectorRef,
	Component,
	ElementRef,
	HostBinding,
	Input,
	NgZone,
	OnDestroy,
	OnInit,
	ViewChild
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
	ActivatedRoute,
	NavigationCancel,
	NavigationEnd,
	NavigationError,
	Router
} from '@angular/router';
import {
	AiAssistantInputComponent,
	IconComponent,
	TypographyComponent
} from 'components';
import { LoButton, LoIcon, LoTooltip } from 'lo-design-system';
import { combineLatest, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { AnaAssistantFacadeService } from '@brand-lolly/app/_services/ana-assistant-facade.service';
import { NotificationsService } from '@brand-lolly/app/_services/notifications/notifications.service';

const ANA_COMPOSER_INSET_VARIABLE = '--ana-composer-inset';
const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';
const EASE_IN_OUT = 'cubic-bezier(0.77, 0, 0.175, 1)';
// CONTRACT: the Ask Ana panel's own open curve, so the ghost travels with the panel.
const PANEL_EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
const EXIT_MS = 160;
const GHOST_MS = 260;
const BURST_MS = 220;
// TRAP: the burst peaks at 30% of its 220ms — an exit that starts sooner fades the
// glyph before the peak is seen.
const TOGGLE_EXIT_DELAY_MS = 60;
const MORPH_MS = 300;
const MORPH_EASE = 'cubic-bezier(0.645, 0.045, 0.355, 1)';
const CONTENT_OUT_MS = 80;
const CONTENT_IN_MS = 100;
const CROSSFADE_OUT_MS = 100;
const CROSSFADE_IN_MS = 150;
// CONTRACT: everything in the pill but the toggle — the textarea and the send button are
// the library input's own.
const MORPH_CONTENT =
	'.composer-leading, .composer-divider, textarea, .send-btn';

// TRAP: module scope, not a field — the slide-up belongs to the app's first render, and a
// field would replay it whenever the shell hosting this composer is re-created.
let composerHasArrived = false;

@Component({
	standalone: true,
	selector: 'app-ana-page-composer',
	templateUrl: './ana-page-composer.component.html',
	styleUrls: ['./ana-page-composer.component.scss'],
	imports: [
		NgIf,
		AiAssistantInputComponent,
		IconComponent,
		TypographyComponent,
		LoButton,
		LoIcon,
		LoTooltip
	]
})
export class AnaPageComposerComponent implements OnInit, OnDestroy {
	@Input({ required: true }) insetHost!: HTMLElement;

	@ViewChild('bar') bar?: ElementRef<HTMLElement>;
	@ViewChild('shell', { read: ElementRef }) shell?: ElementRef<HTMLElement>;

	// TRAP: a fresh input is one row high whatever its restored draft holds, so a
	// multi-line draft would come back collapsed — it is re-measured on every mount.
	@ViewChild(AiAssistantInputComponent)
	set pageInput(input: AiAssistantInputComponent | undefined) {
		this.input = input;
		input?.autoResize();
	}

	readonly messageControl = new FormControl<string>('', {
		nonNullable: true
	});

	barRendered = false;
	compact = false;
	contentHidden = false;
	awaitingArrival = !composerHasArrived;
	arriving = false;
	leaving = false;
	returning = false;
	heldByPage = false;
	// TRAP: stays true until visible$ — before the panel first binds, the facade parks ONE
	// message and a second send would overwrite it.
	sending = false;
	badgeCount = 0;
	openLabel = 'Open Ana';

	// TRAP: follows barRendered, not whether the toggle should show — the host must stay
	// laid out through the exit, or the inset drops to 0 and the page's bottom bars jump.
	@HostBinding('class.ana-page-composer--empty')
	get empty(): boolean {
		return !this.barRendered;
	}

	// TRAP: follows the TARGET form and route, not the morph — the band fades alongside
	// the shell instead of lingering until it settles. A lone circle needs no full-width
	// band behind it: it would only hide the page's bottom edge.
	@HostBinding('class.ana-page-composer--floating')
	get floating(): boolean {
		return !this.reserveSpace || this.targetCompact;
	}

	private input?: AiAssistantInputComponent;
	private routeHidesInput = false;
	private routeHidesComposer = false;
	private reserveSpace = true;
	private targetCompact = false;
	private morphing = false;
	private morphAnimations: Animation[] = [];
	private morphFrame: number | null = null;
	private morphFrom: { box: DOMRect; opacities: string[] } | null = null;
	private restingHeight = 0;
	private publishedInset: string | null = null;
	private panelVisible = false;
	private panelExpanded = false;
	private focusPanelWhenVisible = false;
	private resizeObserver?: ResizeObserver;
	private exitAnimations: Animation[] = [];
	private openedByToggle = false;
	private readonly unsubscriber = new Subject<void>();

	constructor(
		private anaFacade: AnaAssistantFacadeService,
		private notificationsService: NotificationsService,
		private router: Router,
		private host: ElementRef<HTMLElement>,
		private zone: NgZone,
		private changeDetector: ChangeDetectorRef
	) {}

	ngOnInit(): void {
		this.readRouteFlags();

		// TRAP: settled navigations only — a NavigationStart reset, as trackShellBackground
		// does, flashes the input on every navigation away from a page that hides it.
		this.router.events
			.pipe(
				filter(
					event =>
						event instanceof NavigationEnd ||
						event instanceof NavigationCancel ||
						event instanceof NavigationError
				),
				takeUntil(this.unsubscriber)
			)
			.subscribe(() => {
				this.readRouteFlags();
				this.applyVisibility();
				this.publishInset();
				this.arrive();
			});

		combineLatest([
			this.anaFacade.visible$,
			this.anaFacade.expanded$,
			this.anaFacade.slotHolder$
		])
			.pipe(takeUntil(this.unsubscriber))
			.subscribe(([visible, expanded, holder]) => {
				this.panelVisible = visible;
				this.panelExpanded = expanded;
				this.heldByPage = holder === 'page';
				if (visible) {
					this.sending = false;
					if (this.focusPanelWhenVisible) {
						this.focusPanelWhenVisible = false;
						this.focusPanelInput();
					}
				}
				this.applyVisibility();
			});

		this.notificationsService.badgeCount$
			.pipe(takeUntil(this.unsubscriber))
			.subscribe(count => {
				this.badgeCount = count;
				this.openLabel = count
					? `Open Ana, ${count} new suggestions`
					: 'Open Ana';
			});

		this.observeSizes();

		// TRAP: this composer can be created after the first navigation has settled, and
		// then no settled event would ever release the bar from its pre-arrival state.
		if (this.router.navigated) {
			this.arrive();
		}
	}

	ngOnDestroy(): void {
		this.cancelMorphAnimations();
		this.exitAnimations.forEach(animation => animation.cancel());
		this.resizeObserver?.disconnect();
		this.insetHost?.style.removeProperty(ANA_COMPOSER_INSET_VARIABLE);
		this.unsubscriber.next();
		this.unsubscriber.complete();
	}

	send(): void {
		if (this.sending) return;
		const text = this.messageControl.value.trim();
		if (!text) return;
		this.sending = true;
		this.openedByToggle = false;
		this.focusPanelWhenVisible = true;
		// ORDER: the ghost copies the field's text and box, so it goes before the clear.
		this.launchGhost();
		this.messageControl.setValue('');
		this.anaFacade.openWithMessage(text, { expand: false });
	}

	open(event: MouseEvent): void {
		if (this.heldByPage) return;
		this.openedByToggle = true;
		this.burstIcon(event.currentTarget as HTMLElement);
		this.anaFacade.show();
	}

	private applyVisibility(): void {
		// TRAP: visible$ is the panel's persisted preference and stays true while a page
		// holds the conversation and the shell renders no panel — so it is not what hides
		// the toggle there. A page holding the conversation already IS the chat, and a
		// second entry into it beside the first is one too many: nothing is drawn.
		const panelOnScreen =
			(this.panelVisible || this.panelExpanded) && !this.heldByPage;
		const toggleShown =
			!panelOnScreen && !this.heldByPage && !this.routeHidesComposer;
		const inputShown =
			toggleShown && !this.heldByPage && !this.routeHidesInput;

		if (toggleShown) {
			// TRAP: not while it arrives — the slide-up owns the bar's box, and a morph under
			// it would start from a shell that is still off screen.
			const onScreen =
				this.barRendered &&
				!this.leaving &&
				!this.awaitingArrival &&
				!this.arriving;
			this.stopExit();
			this.revealField();
			this.barRendered = true;
			this.setForm(!inputShown, onScreen);
		} else if (this.barRendered && !this.leaving) {
			this.startExit();
		}
	}

	onBarAnimationEnd(event: AnimationEvent): void {
		if (event.target === event.currentTarget) {
			this.arriving = false;
		}
	}

	private arrive(): void {
		if (composerHasArrived) return;
		composerHasArrived = true;
		this.awaitingArrival = false;
		this.arriving = this.barRendered && !this.leaving;
	}

	private setForm(compact: boolean, animate: boolean): void {
		if (compact === this.targetCompact) return;
		this.targetCompact = compact;
		if (compact) {
			this.keepFocusOnToggle();
		}
		const shell = this.shell?.nativeElement;
		if (!animate || !shell) {
			this.settleForm();
		} else if (this.reducedMotion()) {
			this.crossfadeForm(shell);
		} else {
			this.morphForm(shell);
		}
	}

	private settleForm(): void {
		this.cancelMorphAnimations();
		this.morphing = false;
		this.compact = this.targetCompact;
		this.contentHidden = this.compact;
		if (!this.compact) {
			this.input?.autoResize();
		}
		this.publishInset();
	}

	private morphForm(shell: HTMLElement): void {
		const compact = this.targetCompact;
		const content = Array.from(
			shell.querySelectorAll<HTMLElement>(MORPH_CONTENT)
		);
		// ORDER: measured before the form changes and before a morph in flight is dropped —
		// an interrupted morph continues from wherever the shell is on screen.
		// TRAP: a second change before the frame reuses the FIRST measurement — by then the
		// shell may already be laid out in the new form, which is not what is on screen.
		const from =
			this.morphFrame !== null && this.morphFrom
				? this.morphFrom
				: {
						box: shell.getBoundingClientRect(),
						opacities: content.map(element =>
							this.contentHidden
								? '0'
								: getComputedStyle(element).opacity
						)
				  };
		this.cancelMorphAnimations();
		this.morphFrom = from;
		this.morphing = true;
		this.compact = compact;
		this.contentHidden = false;
		if (!compact) {
			// TRAP: a textarea measured while hidden is sized to 0px — re-measured as it shows.
			this.input?.autoResize();
		}
		this.zone.runOutsideAngular(() => {
			this.morphFrame = requestAnimationFrame(() => {
				this.morphFrame = null;
				this.morphFrom = null;
				this.changeDetector.detectChanges();
				const to = shell.getBoundingClientRect();
				// ORDER: read while the shell is laid out in its target form and before the
				// animation moves it — this is the inset the page keeps for the whole morph.
				this.restingHeight = this.host.nativeElement.offsetHeight;
				this.publishInset();
				// TRAP: the circle's row is right-aligned and the pill's is centred, so that is
				// the point the layout keeps fixed while the width changes.
				const offset = compact
					? from.box.right - to.right
					: from.box.left +
					  from.box.width / 2 -
					  (to.left + to.width / 2);
				// TRAP: no end keyframe — the box ends on the CSS width, so a window resized
				// mid-morph does not snap when the animation lets go.
				const box = shell.animate(
					[
						{
							width: `${from.box.width}px`,
							height: `${from.box.height}px`,
							transform: `translateX(${offset}px)`,
							offset: 0
						}
					],
					{ duration: MORPH_MS, easing: MORPH_EASE }
				);
				const fades = content.map((element, index) =>
					compact
						? element.animate(
								[
									{ opacity: from.opacities[index] },
									{ opacity: 0 }
								],
								{
									duration: CONTENT_OUT_MS,
									easing: 'ease-out',
									fill: 'forwards'
								}
						  )
						: // TRAP: no end keyframe — it fades in to the element's own resting
						  // opacity, which is 0 for a send arrow hidden over an empty field.
						  element.animate(
								[{ opacity: from.opacities[index], offset: 0 }],
								{
									duration: CONTENT_IN_MS,
									delay: MORPH_MS - CONTENT_IN_MS,
									easing: 'ease-out',
									fill: 'backwards'
								}
						  )
				);
				const animations = [box, ...fades];
				this.morphAnimations = animations;
				Promise.all(animations.map(animation => animation.finished))
					.then(() =>
						this.zone.run(() => {
							if (this.morphAnimations !== animations) return;
							this.morphing = false;
							this.contentHidden = this.compact;
							this.changeDetector.detectChanges();
							// ORDER: after the content is hidden — cancelling a forwards fade first
							// would show the pill's content inside the circle for a frame.
							this.cancelMorphAnimations();
							this.publishInset();
						})
					)
					// TRAP: cancel() REJECTS finished — that is an interruption, not an error.
					.catch(() => undefined);
			});
		});
	}

	private crossfadeForm(shell: HTMLElement): void {
		const from = getComputedStyle(shell).opacity;
		this.cancelMorphAnimations();
		this.morphing = true;
		const fadeOut = shell.animate([{ opacity: from }, { opacity: 0 }], {
			duration: CROSSFADE_OUT_MS,
			easing: 'ease-out',
			fill: 'forwards'
		});
		this.morphAnimations = [fadeOut];
		fadeOut.finished
			.then(() =>
				this.zone.run(() => {
					if (this.morphAnimations[0] !== fadeOut) return;
					this.compact = this.contentHidden = this.targetCompact;
					if (!this.compact) {
						this.input?.autoResize();
					}
					this.changeDetector.detectChanges();
					this.restingHeight = this.host.nativeElement.offsetHeight;
					this.publishInset();
					const fadeIn = shell.animate(
						[{ opacity: 0 }, { opacity: 1 }],
						{
							duration: CROSSFADE_IN_MS,
							easing: 'ease-out'
						}
					);
					fadeOut.cancel();
					this.morphAnimations = [fadeIn];
					return fadeIn.finished.then(() =>
						this.zone.run(() => {
							if (this.morphAnimations[0] !== fadeIn) return;
							this.morphAnimations = [];
							this.morphing = false;
							this.publishInset();
						})
					);
				})
			)
			.catch(() => undefined);
	}

	private cancelMorphAnimations(): void {
		if (this.morphFrame !== null) {
			cancelAnimationFrame(this.morphFrame);
			this.morphFrame = null;
		}
		this.morphFrom = null;
		const animations = this.morphAnimations;
		this.morphAnimations = [];
		animations.forEach(animation => animation.cancel());
	}

	private startExit(): void {
		if (this.morphAnimations.length) {
			// TRAP: paused, not settled — settling snaps the shell to its end size under the
			// exit fade. It resumes if the panel closes mid-exit, and settles once gone.
			this.morphAnimations.forEach(animation => animation.pause());
		} else {
			this.settleForm();
		}
		const bar = this.bar?.nativeElement;
		if (!bar) {
			this.barRendered = false;
			return;
		}
		const delay = this.openedByToggle ? TOGGLE_EXIT_DELAY_MS : 0;
		this.openedByToggle = false;
		// TRAP: an exit during the slide-up starts where the bar is — from rest, it would
		// jump up to full opacity first.
		const arrivalFrom = this.arriving ? getComputedStyle(bar) : null;
		const fromOpacity = arrivalFrom?.opacity ?? '1';
		const fromTransform = arrivalFrom?.transform ?? 'translateY(0)';
		this.arriving = false;
		this.leaving = true;
		if (this.exitAnimations.length) {
			// TRAP: a close mid-exit left these running backwards — turn them forward again;
			// a fresh pair would restart from full opacity and the bar would blink.
			this.exitAnimations.forEach(animation => animation.reverse());
			return;
		}
		const timing: KeyframeAnimationOptions = {
			duration: EXIT_MS,
			delay,
			fill: 'forwards'
		};
		// TRAP: two animations, not one — a keyframe's easing covers every property in it,
		// and the fade eases differently from the translate.
		const animations = [
			bar.animate([{ opacity: fromOpacity }, { opacity: 0 }], {
				...timing,
				easing: 'ease-out'
			})
		];
		if (!this.reducedMotion()) {
			animations.push(
				bar.animate(
					[
						{ transform: fromTransform },
						{ transform: 'translateY(0.5rem)' }
					],
					{ ...timing, easing: EASE_OUT }
				)
			);
		}
		this.exitAnimations = animations;
		Promise.all(animations.map(animation => animation.finished))
			.then(() =>
				this.zone.run(() => {
					if (this.exitAnimations !== animations) return;
					this.exitAnimations = [];
					if (this.leaving) {
						this.settleForm();
						this.leaving = false;
						this.returning = true;
						this.barRendered = false;
					} else {
						// TRAP: reversed back to rest, the forwards fill still owns the bar.
						animations.forEach(animation => animation.cancel());
					}
				})
			)
			// TRAP: cancel() REJECTS finished — that is ngOnDestroy, not an error.
			.catch(() => undefined);
	}

	private stopExit(): void {
		if (!this.leaving) return;
		this.leaving = false;
		// TRAP: reverse, never cancel — the bar animates back instead of snapping, and a
		// cancel sweeping the element's animations would also stop the rim's rotation.
		this.exitAnimations.forEach(animation => animation.reverse());
		this.morphAnimations.forEach(animation => animation.play());
	}

	private keepFocusOnToggle(): void {
		const shell = this.shell?.nativeElement;
		const focused = document.activeElement;
		if (!shell || !focused || !shell.contains(focused)) return;
		const toggle = shell.querySelector<HTMLElement>('.composer-open');
		if (!toggle || toggle.contains(focused)) return;
		// TRAP: the field is hidden once the circle settles, and focus left inside it would
		// drop to the body — a keyboard user would lose their place.
		toggle.focus({ preventScroll: true });
	}

	private revealField(): void {
		this.host.nativeElement
			.querySelector<HTMLTextAreaElement>('.composer-input textarea')
			?.style.removeProperty('opacity');
	}

	private launchGhost(): void {
		const field =
			this.host.nativeElement.querySelector<HTMLTextAreaElement>(
				'.composer-input textarea'
			);
		if (!field) return;
		const box = field.getBoundingClientRect();
		const fieldStyle = getComputedStyle(field);
		const ghost = document.createElement('div');
		ghost.setAttribute('aria-hidden', 'true');
		ghost.textContent = field.value;
		Object.assign(ghost.style, {
			position: 'fixed',
			left: `${box.left}px`,
			top: `${box.top}px`,
			width: `${box.width}px`,
			height: `${box.height}px`,
			boxSizing: 'border-box',
			overflow: 'hidden',
			whiteSpace: 'pre-wrap',
			overflowWrap: fieldStyle.overflowWrap,
			wordBreak: fieldStyle.wordBreak,
			padding: fieldStyle.padding,
			fontFamily: fieldStyle.fontFamily,
			fontSize: fieldStyle.fontSize,
			fontWeight: fieldStyle.fontWeight,
			letterSpacing: fieldStyle.letterSpacing,
			lineHeight: fieldStyle.lineHeight,
			color: fieldStyle.color,
			pointerEvents: 'none',
			transformOrigin: '100% 50%',
			// CONTRACT: above the panel (1000 when fixed) — it flies toward it.
			zIndex: '1001'
		});
		// TRAP: on body, not in this host — the host goes display:none when the exit ends,
		// 100ms before the ghost does.
		document.body.appendChild(ghost);
		// ORDER: after the append — a detached element has no scroll box to set.
		ghost.scrollTop = field.scrollTop;
		// TRAP: the cleared field would show its placeholder under the departing ghost;
		// hidden until the field is destroyed, or revealed if the panel closes first.
		field.style.opacity = '0';
		const frames: Keyframe[] = this.reducedMotion()
			? [{ opacity: 1 }, { opacity: 0 }]
			: [
					{ opacity: 1, transform: 'none', easing: PANEL_EASE },
					{ opacity: 1, offset: 0.3 },
					{
						opacity: 0,
						transform: 'translate(3rem, -1.5rem) scale(0.96)'
					}
			  ];
		this.zone.runOutsideAngular(() =>
			ghost
				.animate(frames, {
					duration: GHOST_MS,
					easing: 'linear',
					fill: 'forwards'
				})
				.finished.finally(() => ghost.remove())
				.catch(() => undefined)
		);
	}

	private burstIcon(button: HTMLElement): void {
		if (this.reducedMotion()) return;
		const icon = button.querySelector('app-icon');
		this.zone.runOutsideAngular(() =>
			icon?.animate(
				[
					{ transform: 'none', easing: EASE_OUT },
					{
						transform: 'rotate(-12deg) scale(1.12)',
						offset: 0.3,
						easing: EASE_IN_OUT
					},
					{ transform: 'none' }
				],
				{ duration: BURST_MS, easing: 'linear' }
			)
		);
	}

	private reducedMotion(): boolean {
		return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	}

	private readRouteFlags(): void {
		this.routeHidesInput = this.routeTurnsOff('anaComposer');
		this.routeHidesComposer = this.routeTurnsOn('anaComposerHidden');
		this.reserveSpace = !this.routeTurnsOff('anaComposerReserve');
	}

	private routeTurnsOn(flag: string): boolean {
		let route: ActivatedRoute | null = this.router.routerState.root;
		let on = false;
		while (route) {
			const value = route.snapshot.data?.[flag];
			if (typeof value === 'boolean') {
				on = value;
			}
			route = route.firstChild;
		}
		return on;
	}

	private routeTurnsOff(flag: string): boolean {
		let route: ActivatedRoute | null = this.router.routerState.root;
		let off = false;
		while (route) {
			const value = route.snapshot.data?.[flag];
			if (typeof value === 'boolean') {
				off = value === false;
			}
			route = route.firstChild;
		}
		return off;
	}

	private publishInset(): void {
		// TRAP: mid-morph the host is between forms — the target height read when the morph
		// began stands in for it, so the inset moves once, not every frame.
		if (!this.morphing) {
			// TRAP: the host's top padding is only the fade above the pill — content may scroll under it, so it is left out of the inset; counting it left a wide empty band above the composer on every page.
			const host = this.host.nativeElement;
			this.restingHeight =
				host.offsetHeight -
				parseFloat(getComputedStyle(host).paddingTop);
		}
		const inset = this.reserveSpace ? `${this.restingHeight}px` : '0px';
		if (inset === this.publishedInset) return;
		this.publishedInset = inset;
		this.insetHost.style.setProperty(ANA_COMPOSER_INSET_VARIABLE, inset);
	}

	private observeSizes(): void {
		const element = this.host.nativeElement;
		// CONTRACT: the shell mounts this as a direct child of its scroller.
		const scroller = element.parentElement as HTMLElement;
		this.zone.runOutsideAngular(() => {
			this.resizeObserver = new ResizeObserver(() => {
				if (!this.morphing) {
					this.publishInset();
				}
				element.style.right = `${
					scroller.offsetWidth - scroller.clientWidth
				}px`;
			});
			this.resizeObserver.observe(element);
			this.resizeObserver.observe(scroller);
		});
	}

	private focusPanelInput(): void {
		// CONTRACT: the panel's input is the library chat's textarea, and the facade
		// exposes no focus call — so it is found in the shell's panel host.
		requestAnimationFrame(() =>
			document
				.querySelector<HTMLTextAreaElement>(
					'app-ana-assistant-panel app-ai-assistant-input textarea'
				)
				?.focus({ preventScroll: true })
		);
	}
}
